import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { User, Mail, Lock, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import SeoHead from "@/components/seo/SeoHead";

const Register = () => {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await register({ ...form, role: "buyer" });
      toast.success(`Welcome to FarmersAgroHub, ${u.name}!`);
      navigate("/dashboard");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="container-fah py-10 sm:py-16 flex justify-center" data-testid="page-register">
      <SeoHead title="Create Account — Free Buyer Signup" description="Create your free buyer account on FarmersAgroHub and start shopping from thousands of verified agriculture sellers across India." path="/register" />
      <div className="card-fah p-6 sm:p-8 max-w-md w-full">
        <h2 className="font-display text-2xl font-bold">{t("Create Buyer Account")}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t("Join thousands of farmers shopping smarter")}</p>

        {err && <div className="mt-4 text-sm bg-destructive/10 text-destructive p-3 rounded-lg" data-testid="register-error">{err}</div>}

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <Field icon={User} label={t("Full Name")} testid="input-name" type="text" value={form.name} onChange={onChange("name")} placeholder="Ramesh Patel" required />
          <Field icon={Mail} label={t("Email")} testid="input-email" type="email" value={form.email} onChange={onChange("email")} placeholder="you@example.com" required />
          <Field icon={Phone} label={t("Mobile")} testid="input-phone" type="tel" value={form.phone} onChange={onChange("phone")} placeholder="+91 98765 43210" />
          <Field icon={Lock} label={t("Password")} testid="input-password" type="password" value={form.password} onChange={onChange("password")} placeholder={t("Min. 6 characters")} required />

          <button disabled={loading} type="submit" className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="btn-register-submit">
            {loading ? t("Creating account…") : <>{t("Create Account")} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <div className="mt-5 text-center text-sm">
          {t("Already have an account?")} <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="link-login-from-register">{t("Sign in")}</Link>
        </div>
        <div className="mt-3 text-center text-xs text-muted-foreground">
          {t("Want to sell?")} <Link to="/seller/register" className="text-primary hover:underline">{t("Register as Seller")}</Link>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, testid, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1 flex items-center border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
      <Icon className="w-4 h-4 text-muted-foreground ml-3" />
      <input data-testid={testid} {...props} className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm" />
    </div>
  </div>
);

export default Register;
